import { useState, useEffect, useRef } from "react";
import { request } from "hooks/useOkoApi/useOkoApi";

const EMPTY_FACETS = {
  total: 0,
  standards: [],
  learning_components: [],
  question_types: { multiple_choice: 0, open_response: 0 },
};

// Build the question-facets query from the current partial selection. Each facet in
// the response "varies its own dimension and applies the others", so we always send
// the full current selection and let the server compute cascading availability.
const buildFacetsQuery = (filter = {}) => {
  const params = new URLSearchParams();
  if (filter.standardCode) params.set("standard_code", filter.standardCode);
  (filter.standards || []).forEach((code) => code && params.append("standards", code));
  (filter.learningComponents || []).forEach((lc) => lc && params.append("learning_component", lc));
  if (filter.questionType && filter.questionType !== "both") params.set("question_type", filter.questionType);
  return params.toString();
};

// Shared polling body for both scopes below: debounced fetch of a question-facets
// endpoint, keyed on `scopeId` (a lessonId or a standardCode) + the filter's values.
// `urlPrefix` is a stable string ("curriculum/lessons" | "curriculum/standards"), not a
// function, so it can sit in the effect's dependency array as-is.
//
// `inFlightRef` (keyed by scopeId + filterKey) holds the shared promise for whichever
// physical request is currently outstanding for a given key. If the educator navigates
// away and back to the same key before that request resolves, the re-entrant effect
// joins the SAME in-flight promise instead of firing a new physical GET (OKO2-3534) —
// every subscriber for that key still gets the eventual result, just without stacking
// up duplicate requests. Each effect instance's own `cancelled` flag keeps a response
// from writing state once that instance's key is no longer the active one.
const useFacetsFromUrl = (scopeId, filter, urlPrefix, { debounceMs = 250, enabled = true } = {}) => {
  const [facets, setFacets] = useState(EMPTY_FACETS);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [error, setError] = useState(false);
  // Unreactive mirror of hasLoaded, read by the debounce decision below. Reading the state
  // there instead would either re-run the fetch effect on first success (a duplicate
  // request) or trip react-hooks/exhaustive-deps, which CI=true promotes to a build error.
  const hasLoadedRef = useRef(false);
  // Shared in-flight promises, keyed by `${scopeId}::${filterKey}`. Lives for the hook
  // instance's lifetime (NOT reset on scope change) so a still-pending request for a key
  // gets reused if that key becomes active again before it resolves, instead of firing a
  // second physical GET (OKO2-3534). Each effect instance's own `cancelled` flag below
  // still keeps a late response from writing state once that instance's key is stale.
  const inFlightRef = useRef(new Map());
  // Serialize so the effect keys on the filter's values, not its object identity.
  const filterKey = JSON.stringify(filter || null);

  // `hasLoaded` may only be true while `facets` came from a real response. Facets are wiped
  // both here and in the fetch effect's early return, so this keys on everything that
  // triggers a wipe: a scope change AND the hook being switched off (the panel closing).
  // Keying on scopeId alone would leave hasLoaded true over EMPTY_FACETS after a close/
  // reopen, rendering placeholder zeros as a real "no questions" answer. Declared before
  // the fetch effect so the ref is already reset when that effect runs in the same commit.
  useEffect(() => {
    hasLoadedRef.current = false;
    setFacets(EMPTY_FACETS);
    setHasLoaded(false);
    setError(false);
  }, [scopeId, enabled]);

  useEffect(() => {
    if (!enabled || !scopeId) {
      setFacets(EMPTY_FACETS);
      return undefined;
    }
    const activeFilter = JSON.parse(filterKey);
    const key = `${scopeId}::${filterKey}`;
    let cancelled = false;
    setLoading(true);
    // The debounce guards against rapid multi-select toggling; it is pure added latency
    // while the panel has nothing to show yet.
    const delay = hasLoadedRef.current ? debounceMs : 0;
    const timer = setTimeout(async () => {
      let inFlight = inFlightRef.current.get(key);
      if (!inFlight) {
        const qs = buildFacetsQuery(activeFilter);
        // Promise.resolve(...) wraps the call the same way `await` would: it's a no-op
        // if `request` already returns a promise, but keeps this from throwing if a
        // caller's mock (or a future implementation) ever returns a bare value instead.
        inFlight = Promise.resolve(
          request(
            `${urlPrefix}/${scopeId}/question-facets${qs ? `?${qs}` : ""}`,
            { method: "GET" },
          ),
        ).finally(() => {
          inFlightRef.current.delete(key);
        });
        inFlightRef.current.set(key, inFlight);
      }
      try {
        const data = await inFlight;
        if (cancelled) return;
        setFacets({
          total: typeof data?.total === "number" ? data.total : 0,
          standards: data?.standards || [],
          learning_components: data?.learning_components || [],
          question_types: data?.question_types || EMPTY_FACETS.question_types,
        });
        hasLoadedRef.current = true;
        setHasLoaded(true);
        setError(false);
      } catch {
        // Keep the last good facets. Zeroing them here is indistinguishable from "this
        // lesson has no questions", which is the one thing the panel must never guess.
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [scopeId, filterKey, urlPrefix, debounceMs, enabled]);

  return { facets, loading, hasLoaded, error };
};

// Cascading facet availability for the Advanced Selection filter UI.
// GET /curriculum/lessons/{lessonId}/question-facets — given the teacher's current
// partial selection, returns what still has questions so dead options can be disabled
// (the primary "never empty" mechanism) and live counts shown.
//
// `filter` — { standardCode?, standards?, learningComponents?, questionType? }.
// The fetch is DEBOUNCED (default 250ms) so rapid multi-select toggling doesn't spam
// the endpoint — except while nothing is on screen yet, where the debounce is pure latency.
// Returns { facets, loading, hasLoaded, error }; facets keeps its last value while a new
// request is in flight, and on failure (no flicker to zero).
//
// Consumers rendering counts or disabled states want `hasLoaded && !error`, NOT `loading`:
// `loading` is true on every interaction, so gating on it makes counts blink and greys out
// controls mid-click.
export const useQuestionFacets = (lessonId, filter, opts) =>
  useFacetsFromUrl(lessonId, filter, "curriculum/lessons", opts);

// Standard-flow counterpart of useQuestionFacets
// — GET /curriculum/standards/{standardCode}/question-facets. Identical param shape and
// response model; only the URL's scope id changes (a standard code instead of a lesson id).
export const useStandardQuestionFacets = (standardCode, filter, opts) =>
  useFacetsFromUrl(standardCode, filter, "curriculum/standards", opts);
